import { Award, FileText, TreePine, CheckCircle, Clipboard, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ArboristSection() {
  return (
    <section
      id="arborist"
      className="py-28 lg:py-36 bg-white"
      data-testid="arborist-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
            ISA Certified Professionals
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1628] mb-6">
            Arborists vs.
            <br />
            <span className="gradient-text">Tree Trimmers</span>
          </h2>
          <p className="text-[#64748B] max-w-3xl mx-auto text-lg">
            Understanding the difference between arborists and tree trimmers
            helps you choose the right professional for your tree care needs.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* ISA Certified Arborists */}
          <div className="bg-[#F8F6F1] p-10 border-t-4 border-[#10B981]">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#0A1628]">
                ISA Certified Arborists
              </h3>
            </div>

            <p className="text-[#64748B] mb-8 text-lg leading-relaxed">
              <strong className="text-[#0A1628]">Arborists</strong>, also known as <strong className="text-[#0A1628]">tree surgeons</strong>, 
              are highly trained professionals with extensive knowledge in the science 
              of arboriculture. Equipped with a deep understanding of tree biology, 
              root systems, and structural design, arborists excel in diagnosing and 
              treating tree health issues.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white p-5 border-l-4 border-[#10B981]">
                <Clipboard className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-[#0A1628]">Health Diagnosis</p>
                  <p className="text-[#64748B] text-sm">
                    Expert in diagnosing tree diseases, pest infestations, and structural problems
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white p-5 border-l-4 border-[#10B981]">
                <TreePine className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-[#0A1628]">Tree Biology Experts</p>
                  <p className="text-[#64748B] text-sm">
                    Deep understanding of tree physiology, growth patterns, and environmental needs
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white p-5 border-l-4 border-[#10B981]">
                <Shield className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-[#0A1628]">Comprehensive Solutions</p>
                  <p className="text-[#64748B] text-sm">
                    Focus on overall tree health, sustainability, and long-term care planning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Tree Trimmers */}
          <div className="bg-[#F8F6F1] p-10 border-t-4 border-[#D4A853]">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D4A853] to-[#B8923A] flex items-center justify-center">
                <TreePine className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#0A1628]">
                Professional Tree Trimmers
              </h3>
            </div>

            <p className="text-[#64748B] mb-6 text-lg leading-relaxed">
              <strong className="text-[#0A1628]">Tree trimmers</strong>, also referred to as <strong className="text-[#0A1628]">tree pruners</strong> or 
              technicians, concentrate primarily on the visual appeal of trees. Their main 
              objective is to shape trees and maintain their desired size, enhancing their 
              overall appearance.
            </p>

            <p className="text-[#64748B] mb-6 text-lg leading-relaxed">
              Tree trimmers use various tools, such as pole saws, hand shears, and hedge 
              trimmers, to efficiently remove dead, damaged, or diseased branches. They 
              play a crucial role in improving tree aesthetics and minimizing potential 
              safety hazards. Our trimmers have <strong className="text-[#10B981]">extensive professional experience</strong>.
            </p>

            <div className="bg-white p-5 border-l-4 border-[#D4A853]">
              <p className="text-[#64748B]">
                Additionally, tree trimmers may be responsible for trimming trees around 
                power lines and other structures, ensuring safety and preventing disruptions.
              </p>
            </div>
          </div>
        </div>

        {/* Arborist Report Section */}
        <div className="mt-20 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] p-10 lg:p-16 relative overflow-hidden">
          {/* Decorative */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#10B981]/5 rounded-full blur-3xl"></div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D4A853] text-[#0A1628] px-5 py-2 text-xs font-bold uppercase tracking-wider mb-6">
                <FileText className="w-4 h-4" />
                Official Documentation
              </div>
              
              <h3 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-bold text-white mb-6">
                What is an Arborist Report?
              </h3>
              
              <p className="text-white/80 mb-8 text-lg leading-relaxed">
                An <strong className="text-white">Arborist Report</strong> is a professional, 
                certified document prepared by an ISA Certified Arborist after a comprehensive 
                on-site tree inspection. This official assessment evaluates your trees' health, 
                structural integrity, and potential risks.
              </p>

              <h4 className="text-[#10B981] font-bold mb-5 text-lg">What's Included in the Report:</h4>
              
              <div className="space-y-3 mb-8">
                {[
                  "Tree Identification — Species, size (height & trunk diameter), and location on property",
                  "Health Assessment — Canopy density, deadwood presence, signs of disease or pest infestation",
                  "Structural Analysis — Root system condition, structural defects, and stability evaluation",
                  "Risk Assessment — Failure likelihood and potential impact on nearby structures",
                  "Care Recommendations — Watering schedules, maintenance needs, and long-term care plan",
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-[#DC2626]/20 border border-[#DC2626]/40 p-5">
                <p className="text-white">
                  <strong className="text-[#D4A853]">Note:</strong> The written Arborist Health 
                  Assessment Report is available at an additional cost. Contact us for details.
                </p>
              </div>
            </div>

            {/* Sample Report Preview */}
            <div className="bg-white p-8 shadow-2xl">
              <div className="border-b-2 border-[#10B981] pb-5 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-['Cormorant_Garamond'] text-2xl font-bold gradient-text">
                      Celebrity Tree Service
                    </p>
                    <p className="text-xs text-[#64748B]">ISA Certified Arborist</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                </div>
              </div>

              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-2">
                Arborist Tree Inspection
              </p>
              <p className="font-bold text-[#0A1628] text-xl mb-6">
                Written Assessment Report
              </p>

              <div className="space-y-3 text-sm mb-6 pb-6 border-b border-[#10B981]/10">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Property:</span>
                  <span className="text-[#0A1628] font-medium">123 Main Street, AZ</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Client:</span>
                  <span className="text-[#0A1628] font-medium">John Smith</span>
                </div>
              </div>

              <div className="bg-[#F8F6F1] p-5 mb-6">
                <p className="font-bold text-[#10B981] mb-2">Tree #1 — Sweet Acacia</p>
                <p className="text-xs text-[#64748B] mb-3">14" DBH, ~28 ft tall</p>
                <p className="text-sm font-bold text-[#0A1628] mb-2">Findings:</p>
                <ul className="text-xs text-[#64748B] space-y-1 ml-4">
                  <li>• Overall health: Good with full canopy</li>
                  <li>• Minor deadwood in upper crown</li>
                  <li>• No structural defects observed</li>
                  <li>• No active pest or disease signs</li>
                </ul>
                <p className="text-sm mt-4">
                  <strong className="text-[#10B981]">Assessment:</strong>{" "}
                  <span className="text-[#64748B]">Low risk for failure under normal conditions</span>
                </p>
              </div>

              <div className="text-center border-t border-[#10B981]/10 pt-5">
                <p className="text-xs text-[#64748B]">Sample Report Preview — Private info redacted</p>
              </div>
            </div>
          </div>

          {/* Why Get Report */}
          <div className="mt-16 grid md:grid-cols-3 gap-6 relative z-10">
            {[
              {
                title: "Increase Property Value",
                description: "Documented healthy trees can add 10-15% to your property's appraised value",
              },
              {
                title: "Real Estate Transactions",
                description: "Required documentation for home sales, insurance claims, and HOA compliance",
              },
              {
                title: "Liability Protection",
                description: "Official assessment protects you from liability if a tree causes damage",
              },
            ].map((item, index) => (
              <div key={index} className="bg-white/5 p-8 border border-white/10 hover:border-[#10B981]/30 transition-colors">
                <h4 className="text-white font-bold text-lg mb-3">{item.title}</h4>
                <p className="text-white/60">{item.description}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center relative z-10">
            <p className="text-white/80 mb-8 text-lg">
              Schedule your ISA Arborist Assessment today. Next-day appointments available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold shadow-lg"
                data-testid="arborist-cta-schedule"
              >
                <a href="#contact">Schedule Assessment</a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
                data-testid="arborist-cta-call"
              >
                <a href="tel:4803517775">Call (480) 351-7775</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
